import { titleCase } from '@chiroup/core/functions/titleCase';
import { Condition } from '@chiroup/core/types/Condition.type';
import {
  CarePlanComplaint,
  ComplaintStatuses,
  EncounterInfo,
  ImprovementOptionsText,
} from '@chiroup/core/types/PatientVisit.type';
import { ReferenceCodeDisplay } from '@chiroup/core/types/ReferenceCode.type';

interface AssessmentInput {
  complaints: CarePlanComplaint[];
  totalComplaints: number;
  allConditionsRetrieved: { [key: number]: Partial<Condition> };
  totalVisits: number;
  currentVisit: number;
  reevaluation: boolean;
  goal?: number;
  encounterInfo?: EncounterInfo;
  justGoal?: boolean;
}

const buildTreatmentPlanString = (
  totalVisits: number,
  reevaluation: boolean,
  encounterInfo?: EncounterInfo
) => {
  const visitNumber = encounterInfo?.visitNumber;
  if (!encounterInfo || typeof visitNumber === 'undefined') return '';
  const continueAsScheduled = encounterInfo?.visitTimes?.some(
    visitTime => visitTime.duration === 0
  );

  const isInitialVisit = visitNumber === 1;
  const isWithinTotalVisits = visitNumber < totalVisits;
  const lastVisit = visitNumber === totalVisits && !continueAsScheduled;
  const treatmentPlan = isInitialVisit ? getCarePlanSections(encounterInfo) : '';

  if (isInitialVisit) {
    return reevaluation
      ? `Treatment has been updated to include ${treatmentPlan}`
      : `A care plan has been established to include ${treatmentPlan}`;
  }

  if (isWithinTotalVisits && !continueAsScheduled) {
    return `Currently at visit ${visitNumber || 1} of ${totalVisits}, continue care as scheduled.`;
  } else if (lastVisit) {
    return `A treatment plan of <strong>${totalVisits}</strong> visits was completed.`;
  } else if (continueAsScheduled || !isWithinTotalVisits) {
    return `Currently at visit ${visitNumber || 1}, continue care as scheduled.`;
  }

  return '';
};

const getCarePlanSections = (encounterInfo: EncounterInfo) => {
  const visitTimes = encounterInfo?.visitTimes;

  const mappedHTML = visitTimes
    ? visitTimes.map((visitTime, index) => {
        const visitsText = `${visitTime.times} ${visitTime.times === 1 ? 'visit' : 'visits'} per ${
          visitTime.timesUnit
        }`;

        const durationText = visitTime.duration
          ? ` for ${visitTime.duration} ${
              visitTime.duration === 1 ? visitTime.durationUnit : `${visitTime.durationUnit}s`
            }`
          : ' as scheduled';

        const connectorText = index === visitTimes.length - 1 ? '.' : ' then ';

        return `<strong>${visitsText}${durationText}</strong>${connectorText}`;
      })
    : [];

  return `${mappedHTML.join('')}`;
};

const buildGoalString = (
  totalVisits: number,
  currentVisit: number,
  encounterInfo?: EncounterInfo
) => {
  if (!encounterInfo?.goal) return '';

  if (totalVisits === currentVisit) {
    return `Outcome goal: A course of treatment consisting of <strong>${totalVisits} visits</strong> was successfully completed, with an anticipated subjective improvement of <strong>${
      encounterInfo?.goal ?? 0
    }%</strong>.`;
  } else {
    return `Outcome goal: Expect a ${encounterInfo?.goal ?? 0}% subjective improvement${
      encounterInfo?.visitTimes?.length
        ? ` after a care plan of ${getCarePlanSections(encounterInfo)}`
        : '.'
    }`;
  }
};

const buildICDString = (icdCodes: ReferenceCodeDisplay[]) => {
  if (!icdCodes?.length) return '';

  return `<p>Diagnostic Codes:${icdCodes.map(code => {
    const label = code.code + ' - ' + code.description;

    return `<span data-type="codes" class="billing-code rounded-lg
        border border-gray-300 bg-gray-50 px-1"
        data-id=${code.code} data-label="${label}"
        contenteditable="false"
        draggable="true">${label}</span>`;
  })}</p>`;
};

const buildDiagnosisString = (
  complaint: CarePlanComplaint,
  allConditionsRetrieved: { [key: number]: Partial<Condition> }
) => {
  if (!complaint.assessment?.conditions?.length) return '';
  const conditions = (complaint?.assessment?.conditions || [])
    .map(condition => {
      const matchingObject = Object.values(allConditionsRetrieved).find(
        obj => obj.ID === condition.conditionId || obj.parentID === condition.conditionId
      );

      // Fall back to condition.name (stored in V2) if not found in allConditionsRetrieved
      return matchingObject?.name || condition.name || '';
    })
    .filter(name => name); // Filter out empty strings

  if (!conditions.length) return '';

  return `<p>Related diagnoses: <strong>${conditions.join(', ')}</strong></p>`;
};

export const getWYSIWYGAssessmentContentFn = ({
  complaints,
  totalComplaints,
  allConditionsRetrieved,
  totalVisits,
  currentVisit,
  reevaluation,
  encounterInfo,
}: AssessmentInput) => {
  return () => {
    let assessmentStr = '';

    if (complaints?.length) {
      complaints.forEach((complaint, index) => {
        const formattedComplaint = titleCase(complaint.complaint ?? '');
        const conditionsReferenceCodes =
          complaint?.assessment?.conditions?.flatMap(
            condition => condition?.referenceCodes || []
          ) || [];
        const nonConditionsReferenceCodes =
          complaint?.assessment?.nonConditions?.referenceCodes || [];

        const icdCodes = [...conditionsReferenceCodes, ...nonConditionsReferenceCodes];
        assessmentStr += `<h3><span style="font-weight: semi-bold;" >${formattedComplaint}</span></h3>`;

        assessmentStr += `<p>Complaint ${index + 1} of ${totalComplaints}</p>`;

        // Assessment value/status — show for all statuses
        // Note: inactive takes priority over PRN (deactivation is the final state)
        if (
          (complaint.prn || complaint.assessment?.prn) &&
          complaint?.status !== ComplaintStatuses.INACTIVE
        ) {
          assessmentStr += `<p><span style="font-weight: bold;">Treatment plan: ${formattedComplaint}</span>
            has been marked as PRN. No specific treatment plan is established for the patient as their care is provided on an as-needed basis (PRN).</p>`;
        } else if (complaint.assessment?.value) {
          assessmentStr += `<p>Assessment: <span style="font-weight: bold;">${
            ImprovementOptionsText[complaint.assessment.value]
          }</span></p>`;
        }

        // Diagnosis and ICD codes — show for all statuses
        const diagnosisStr = buildDiagnosisString(complaint, allConditionsRetrieved);

        if (diagnosisStr.length) {
          assessmentStr += diagnosisStr;
        }

        const icdString = buildICDString(icdCodes);

        if (icdCodes.length) {
          assessmentStr += icdString;
        }

        // Treatment plan — deactivation message for inactive, normal plan for active non-PRN
        if (complaint?.status === ComplaintStatuses.INACTIVE) {
          assessmentStr += `<p><span style="font-weight: bold;">Treatment plan: ${formattedComplaint}</span>
            has been marked as ${
              complaint?.inactiveReason || 'deactivated'
            }, indicating the conclusion of treatment for the patient.
        </p>`;
        } else if (!complaint.prn && !complaint.assessment?.prn) {
          const treatmentPlanStr = buildTreatmentPlanString(
            totalVisits,
            reevaluation,
            encounterInfo
          );
          assessmentStr += encounterInfo?.visitTimes?.length
            ? `<p>Treatment plan: ${treatmentPlanStr}</p>`
            : '';
        }

        // Assessment notes, show whenever the provider typed one, PRN included
        if (complaint?.assessment?.note) {
          assessmentStr += `<p><span style="font-weight: bold;">Notes: </span><span style="font-weight: normal;">${complaint.assessment.note}</span></p>`;
        }

        // Goal — only for active non-PRN (not relevant for discharged complaints)
        if (
          complaint?.status !== ComplaintStatuses.INACTIVE &&
          !complaint.prn &&
          !complaint.assessment?.prn
        ) {
          const goalStr = buildGoalString(totalVisits, currentVisit, encounterInfo);
          assessmentStr += `<p>${goalStr}</p>`;
        }

        if (encounterInfo?.notes) {
          assessmentStr += `<p>Notes/Functional Goals: ${encounterInfo?.notes}</p>`;
        }
      });
    }

    return (assessmentStr += '<p></p>');
  };
};
